/**
 * The one browser-side answer to "is auth bypassed on this deployment?".
 *
 * ## The build-time constant is `__DEPLOY_ENV__`
 *
 * `vite.config.ts` `define`s `__DEPLOY_ENV__` as a JSON-stringified string from
 * `process.env.VERCEL_ENV`: `'production'` | `'preview'` | `'development'`
 * (unset locally → `'development'`). It is declared in `src/vite-env.d.ts`.
 * **Other modules that need the deploy environment — notably `src/lib/sentry.ts`
 * for Sentry's `environment` — use this same constant.** Do not mint a second one.
 *
 * ## Why this file exists
 *
 * `VITE_AUTH_DISABLED=true` is the preview deployment's bypass of Chronos SSO;
 * access there is gated by Vercel deployment protection instead (see
 * `.claude/rules/branch-strategy.md`). It must never be honoured in production,
 * where it would mint an ADMIN "Preview User" for every visitor. Three layers now
 * stop that: `vite.config.ts` **throws** rather than producing a production build
 * with the flag set, this helper refuses it at runtime anyway, and the server has
 * its own twin in `api/_lib/authMode.ts`.
 *
 * Nothing in `src/` may read `import.meta.env.VITE_AUTH_DISABLED` directly.
 *
 * Browser-only: this module is never reached from a lambda, so it may use
 * `import.meta.env`. If that ever changes, it must move behind an injected value.
 */

export type DeployEnv = 'production' | 'preview' | 'development'

/** Pure decision, so it can be tested without a Vite build. */
export function computeAuthDisabled(
  flag: string | undefined,
  deployEnv: string | undefined
): boolean {
  if (flag !== 'true') return false
  return deployEnv !== 'production'
}

function readDeployEnv(): DeployEnv {
  const raw = typeof __DEPLOY_ENV__ === 'string' ? __DEPLOY_ENV__ : undefined
  return raw === 'production' || raw === 'preview' ? raw : 'development'
}

function readFlag(): string | undefined {
  // `import.meta.env` is undefined outside a Vite build (any plain-Node load),
  // so this is read defensively rather than destructured at module level.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env
  return env?.VITE_AUTH_DISABLED
}

/** `'production' | 'preview' | 'development'` for this build. */
export function deployEnv(): DeployEnv {
  return readDeployEnv()
}

/** True only on a non-production deployment that set `VITE_AUTH_DISABLED=true`. */
export function isAuthDisabled(): boolean {
  return computeAuthDisabled(readFlag(), readDeployEnv())
}
